import { useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { CheckCircle2, XCircle } from 'lucide-react';
import { Button, Card, CardContent, Skeleton } from '@mlm/ui';
import { GatewayPaymentPanel, ManualPaymentForm } from '../components/payment/order-payment-panels';
import { CancelOrderCta } from '../components/payment/cancel-order-cta';
import { getMe, getOrder, getOrderGroup, logout } from '../lib/api';
import {
  isPayPageLocked,
  mySlotsQuery,
  resetLockInputs,
  unpaidOrdersQuery,
} from '../lib/lock-inputs';
import { useAuthStore } from '../lib/auth-store';
import { useBrand, useTenantConfig } from '../storefront/lib/hooks';
import { fmtCurrency } from '../lib/format';

/**
 * `/pay/:orderId` — settle one unpaid order, and nothing else.
 *
 * Deliberately rendered OUTSIDE `<Layout />`: no sidebar, no bottom tab bar, no
 * header nav. A buyer who has just been asked for money should be looking at one
 * decision, not at seventeen places to wander off to.
 *
 * Whether it is a DEAD END is a different question, and it is the lock's to
 * answer — not this page's. It used to offer no exit but signing out to
 * everybody, including members on tenants with `unpaid_order_lock_enabled` off
 * (every live tenant) and members the lock explicitly refuses to hold. That is
 * strictly stricter than the policy the screen exists to serve, and it stranded
 * a member who had already uploaded a proof and simply wanted to go back. So the
 * exits render unless `isPayPageLocked` says this member really is held.
 *
 * It renders the SAME panels as `/orders/:id` (`components/payment/`) rather than
 * its own copy, so a manual method's instructions, QR, group-pay banner and
 * rejected-proof notice can't drift between the two surfaces. What it adds is
 * chrome: the brand mark, the amount, and a paid state.
 *
 * The gateway return URL still points at `/orders/:id?pay=<provider>`, on
 * purpose — a buyer who is NOT locked should come back to their normal order
 * page. A locked buyer is bounced here by the guard.
 */
export function PayPage() {
  const { orderId } = useParams<{ orderId: string }>();
  const navigate = useNavigate();
  const brand = useBrand();
  const qc = useQueryClient();
  const clearSession = useAuthStore((s) => s.clear);

  const q = useQuery({
    queryKey: ['order', orderId],
    queryFn: () => getOrder(orderId!),
    enabled: !!orderId,
    // Same cadence as the order page: a verified gateway webhook lands a beat
    // after the buyer returns, and this screen must not need a manual refresh.
    refetchInterval: (query) => {
      const s = query.state.data?.status;
      return s === 'pending' || s === 'to_pay' ? 3000 : false;
    },
  });
  const order = q.data;

  // One payment can cover several orders (a mixed cart splits into siblings
  // under one `checkout_group_id`). Show the amount actually payable, not this
  // one order's total, or the buyer transfers the wrong figure.
  const groupId = order?.checkoutGroupId ?? null;
  const groupQ = useQuery({
    queryKey: ['order-group', groupId],
    queryFn: () => getOrderGroup(groupId!),
    enabled: !!groupId,
  });
  const group = groupQ.data ?? null;
  const isGroupPay = !!group && group.orders.length > 1 && !group.allPaid;
  const payable = isGroupPay ? group!.payableTotal : (order?.total ?? '0');

  async function signOut() {
    await logout();
    clearSession();
    navigate('/login', { replace: true });
  }

  const awaitingPayment = order?.status === 'pending' || order?.status === 'to_pay';
  const settled = !!order && !awaitingPayment;

  /**
   * Is this member genuinely trapped here, or merely on the payment page?
   *
   * Gated exactly as `RequireOrderSettled` gates its own inputs: a lock-OFF
   * tenant issues NEITHER of the other two queries, so the default path costs
   * nothing. `useTenantConfig` is the hook `useBrand` above already reads, so
   * this is the same cache entry and not a second observer with its own retry
   * policy; both remaining keys are the shared entries the rest of the portal
   * mounts anyway.
   *
   * While those two are still in flight we treat the member as LOCKED. The exits
   * are additive, so showing them a beat late is invisible; showing them and
   * then snatching them away is not.
   */
  const lockEnabled = useTenantConfig().data?.unpaidOrderLockEnabled === true;
  const meQ = useQuery({ queryKey: ['me'], queryFn: getMe, enabled: lockEnabled });
  const slotsQ = useQuery({ ...mySlotsQuery, enabled: lockEnabled });
  const locked =
    (lockEnabled && (meQ.isPending || slotsQ.isPending)) ||
    isPayPageLocked({
      lockEnabled,
      registeredViaCampaign: meQ.data?.registeredViaCampaign,
      hasAnyActiveSlot: (slotsQ.data ?? []).some((sl) => sl.status === 'active'),
      order,
    });

  /**
   * The instant this order stops being unpaid — a verified gateway webhook, an
   * admin approving the proof, an admin cancelling it — the payment lock's cached
   * answer is wrong, and the lock CANNOT notice: `RequireOrderSettled` is
   * unmounted for the whole of this page (its destination is declared outside it
   * so the redirect can't loop), so its queries have no observer and never
   * refetch. Left alone, Continue remounts the guard, the guard reads the frozen
   * data with `isPending: false`, and bounces the member straight back here.
   *
   * In an EFFECT, not the Continue handler: the browser back button, a deep link
   * and the button all have to be covered, and by the time the success card has
   * painted this has already run — so the button needs nothing of its own.
   *
   * Then warm the orders key back up while the member reads the card, so the
   * guard mounts with an answer and skips the splash. `prefetchQuery` swallows
   * its own errors and is NOT awaited: a failed warm leaves the entry settled
   * with no data, which the guard reads as unknown and passes. Fail OPEN —
   * awaiting it here would be the fail-closed variant of this very bug.
   */
  useEffect(() => {
    if (!settled) return;
    resetLockInputs(qc);
    void qc.prefetchQuery(unpaidOrdersQuery);
  }, [qc, settled]);

  return (
    <div className="min-h-screen bg-background">
      <div className="mx-auto w-full max-w-lg px-4 py-8">
        <div className="mb-6 flex items-center justify-between">
          <span className="text-base font-semibold">{brand.siteTitle}</span>
          {/* The one way out that isn't paying. A locked buyer who changes their
              mind must not be trapped in the tab with no affordance at all. */}
          <button
            type="button"
            onClick={signOut}
            className="text-xs text-muted-foreground underline-offset-2 hover:underline"
          >
            Sign out
          </button>
        </div>

        {q.isLoading ? (
          <Card>
            <CardContent className="space-y-3 p-6">
              <Skeleton className="h-5 w-40" />
              <Skeleton className="h-24 w-full" />
            </CardContent>
          </Card>
        ) : q.isError || !order ? (
          <Card>
            <CardContent className="p-6 text-sm">
              <p className="font-medium">We couldn&rsquo;t load this order.</p>
              <Button className="mt-4" variant="outline" onClick={() => navigate('/orders')}>
                Go to my orders
              </Button>
            </CardContent>
          </Card>
        ) : order.status === 'cancelled' || order.status === 'refunded' ? (
          // MUST come before the settled branch below. A cancelled order is not
          // "awaiting payment", so without this it fell through to "Thank you!
          // Your order is settled" — telling a member their cancelled order was
          // paid. Reachable the moment members can cancel their own orders.
          <Card>
            <CardContent className="space-y-4 p-6 text-center">
              <XCircle className="mx-auto h-10 w-10 text-muted-foreground" />
              <div>
                <p className="text-lg font-semibold">
                  This order was {order.status === 'refunded' ? 'refunded' : 'cancelled'}.
                </p>
                <p className="mt-1 text-sm text-muted-foreground">
                  There is nothing left to pay for it.
                </p>
                <p className="mt-2 font-mono text-xs text-muted-foreground/70">{order.code}</p>
              </div>
              <Button
                className="w-full"
                variant="outline"
                onClick={() => navigate('/orders', { replace: true })}
              >
                Go to my orders
              </Button>
            </CardContent>
          </Card>
        ) : !awaitingPayment ? (
          <Card>
            <CardContent className="space-y-4 p-6 text-center">
              <CheckCircle2 className="mx-auto h-10 w-10 text-success" />
              <div>
                <p className="text-lg font-semibold">Thank you!</p>
                {/* Deliberately GENERAL. This page settles an activation kit, a
                    course, and a repeat purchase alike, so the copy can't name
                    what was bought ("kit codes, access" was internal vocabulary
                    a buyer doesn't share) and can't say "welcome" — that reads
                    oddly to a member on their third order. `brandName` is the
                    SHORT brand ("FLX"), not `siteTitle` ("FLX Performance
                    Nutrition"), which would not fit mid-sentence. */}
                <p className="mt-1 text-sm text-muted-foreground">
                  Your order is settled — everything it unlocks is now in your {brand.brandName}{' '}
                  account.
                </p>
                {/* Kept, but demoted: worth having when someone contacts support,
                    not worth being the headline. */}
                <p className="mt-2 font-mono text-xs text-muted-foreground/70">{order.code}</p>
              </div>
              {/* Only offered once there is genuinely nothing left to pay, so the
                  guard can't bounce them straight back here. */}
              <Button className="w-full" onClick={() => navigate('/dashboard', { replace: true })}>
                Continue
              </Button>
            </CardContent>
          </Card>
        ) : (
          <Card>
            <CardContent className="space-y-4 p-6">
              <div>
                <p className="text-sm text-muted-foreground">
                  Order <span className="font-mono">{order.code}</span>
                  {isGroupPay ? ` and ${group!.orders.length - 1} more` : ''}
                </p>
                <p className="text-3xl font-semibold tabular-nums">{fmtCurrency(payable)}</p>
                {/* A proof is already in. The member is still held (an admin has
                    to approve before the slot exists), but the screen must not
                    read as a demand for money they have already sent. The panel
                    below renders the matching "Proof submitted" notice. */}
                {order.paymentPending && (
                  <p className="mt-1 text-sm text-muted-foreground">
                    Paid &mdash; waiting for us to verify it.
                  </p>
                )}
              </div>
              <GatewayPaymentPanel order={order} />
              <ManualPaymentForm order={order} />
              {/* Cancelling releases the guard (the CTA resets the lock's cached
                  inputs), so it stays offered in both states. */}
              <CancelOrderCta
                order={order}
                groupSize={isGroupPay ? group!.orders.length : undefined}
                onCancelled={() => navigate('/orders', { replace: true })}
              />
              {/* For a HELD member this renders nothing and the screen is the
                  dead end the lock documents itself as being — signing out is
                  the only other way off it. For everyone else these are the way
                  back, which matters most right after a proof upload: the money
                  is sent, an admin has to look at it, and there is nothing left
                  to do here. `replace` is deliberately NOT used — leaving this
                  page in history keeps the browser back button pointing at the
                  order they may still want to pay. */}
              {!locked && (
                <div className="flex flex-wrap justify-center gap-2 border-t pt-4">
                  <Button variant="ghost" size="sm" onClick={() => navigate('/orders')}>
                    Go to my orders
                  </Button>
                  <Button variant="ghost" size="sm" onClick={() => navigate('/dashboard')}>
                    Back to dashboard
                  </Button>
                </div>
              )}
            </CardContent>
          </Card>
        )}
      </div>
    </div>
  );
}

export default PayPage;
